import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingCart, Wallet, TrendingUp, Cpu, Package,
  BarChart2, HardHat, Users, UserCog, Shield, Settings,
  LogOut, Menu, X, ChevronDown, Zap, LayoutDashboard, FolderOpen, BookOpen
} from "lucide-react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import { toast } from "sonner";
import WelcomeModal from "./WelcomeModal";
import HeaderBell from "./HeaderBell";
import { roleAllowed, normalizeRole } from "../utils/roles";
import { InstalarAppItem, AvisoInstalarApp, BarraSinConexion } from "./AppInstalable";   // Fix EU
import { useModuleAccess } from "../contexts/ModuleAccessContext";
import { TabsProvider, TabsBar, TabsHost, useTabs } from "../contexts/TabsContext";
import { useAvisoVersionNueva } from "../pwa";   // Fix EU

// Roles de Ingeniería SIN el Proyectista (para ocultarle Clientes/Obras/Catálogo/Presupuestos)
const INGENIERIA_NO_PROYECTISTA = ["Superadministrador", "Administrador", "Gerente Tecnico",
  "Coordinador de Obra", "Director", "Gerencia Técnica", "Jefe de Calidad", "Ingeniería"];

// ─── NAVIGATION TREE ────────────────────────────────────────
const NAV_TREE = [
  {
    id: "compras",
    label: "Compras",
    icon: ShoppingCart,
    accent: "#F97316",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras", "Contador",
            "Director", "Gerencia Técnica", "Gerencia Financiera", "Jefe de Compras", "Jefe de Logística", "Residente de Obra"],
    children: [
      { path: "/compras/materiales", label: "Materiales" },
      { path: "/compras/servicios", label: "Servicios/Alquileres" },   // Fix EI (2026-09-27)
      { path: "/compras/operaciones", label: "Costos indirectos" },   // Fix EI (2026-09-27)
      { path: "/compras/caja-chica", label: "Caja Chica" },
      { path: "/compras/libro-compras", label: "Informe de Compras" },   // Fix EZ (2026-10-05)
      // Fix EF (2026-09-24): "el Formateo Ptos Aprobados ya no tiene utilidad, queremos
      // cambiar a Vinculación de Pedidos Rápidos" (CEN, 24/09).
      { path: "/compras/vinculacion-pedidos-rapidos", label: "Vinculación de Pedidos Rápidos", roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Jefe de Compras", "Coordinador de Obra"] },
      { path: "/compras/comparativa-auxiliar", label: "Comparativa Auxiliar", roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Gerencia Financiera"] },
    ],
  },
  {
    id: "tesoreria",
    label: "Tesorería",
    icon: Wallet,
    accent: "#10B981",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador", "Compras",
            "Director", "Gerencia Técnica", "Gerencia Financiera"],
    children: [
      { path: "/tesoreria/pagos", label: "Pagos" },
      { path: "/tesoreria/cobranzas", label: "Facturación y Cobranza" },   // Fix GY (2026-10-19): nombre pedido por CEN
      { path: "/tesoreria/tercerizados", label: "Tercerizados" },   // Fix EE (2026-09-24)
      { path: "/tesoreria/procesos-bancarios", label: "Procesos Bancarios", roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Financiera", "Contador", "Director"] },
    ],
  },
  {
    id: "finanzas",
    label: "Finanzas",
    icon: TrendingUp,
    accent: "#2E6DA4",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador",
            "Director", "Gerencia Técnica", "Gerencia Financiera", "Accionista", "Gerencia Comercial"],
    children: [
      { path: "/finanzas/real", label: "Flujo Real" },
      { path: "/finanzas/proyectado", label: "Flujo Proyectado" },
      { path: "/finanzas/por-obra", label: "Por Obra" },
      { path: "/finanzas/gastos-admin", label: "Gastos Administrativos" },
      { path: "/finanzas/consolidado", label: "Consolidado Anual", roles: [
        "Superadministrador", "Administrador", "Director", "Contador", "Gerencia Financiera",
      ] },
    ],
  },
  // Fix DS (2026-09-22): "Contabilidad: libros contables, plan de cuentas,
  // balance y cuadro de resultados" (Edson, 21/09). Primera etapa: el plan de
  // cuentas y los dos libros. El Libro de Compras es el mismo de Compras.
  {
    id: "contabilidad",
    label: "Contabilidad",
    icon: BookOpen,
    accent: "#0F766E",
    roles: ["Superadministrador", "Administrador", "Contador", "Gerencia Financiera",
            "Gerente Tecnico", "Gerencia Técnica", "Director"],
    children: [
      { path: "/contabilidad/plan-cuentas", label: "Plan de Cuentas" },
      { path: "/contabilidad/libro-compras", label: "Libro de Compras" },
      { path: "/contabilidad/libro-ventas", label: "Libro de Ventas" },
    ],
  },
  {
    id: "ingenieria",
    label: "Ingeniería",
    icon: Cpu,
    accent: "#8B5CF6",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra",
            "Director", "Gerencia Técnica", "Jefe de Calidad", "Ingeniería", "Proyectista"],
    children: [
      {
        path: "/ingenieria/proyecto",
        label: "Proyectos",
        subItems: [
          { path: "/ingenieria/proyecto", label: "Cómputo" },
          { path: "/ingenieria/computo", label: "Proyecto" },
          { path: "/ingenieria/proyectos/archivos", label: "Archivos Útiles" },
          // Fix CL (2026-09-13): configuración del cómputo automático desde planos.
          { path: "/ingenieria/mapeo-planos", label: "Mapeo de Planos" },
        ],
      },
      // Fix CM (2026-09-13): las tareas del flujo de trabajo, con su pantalla global.
      // Fix DO (2026-09-22): el tablero completo pasa al Dashboard. El Proyectista no
      // tiene Dashboard, así que para él el acceso sigue acá.
      // Fix EM (2026-09-28): Ingeniería tiene su apartado de Tareas (Gabinete), para todos
      // los perfiles del módulo; el Proyectista lo tiene en lugar del tablero general.
      { path: "/ingenieria/tareas", label: "Tareas" },
      { path: "/ingenieria/clientes", label: "Clientes", roles: INGENIERIA_NO_PROYECTISTA },
      { path: "/ingenieria/obras", label: "Obras", roles: INGENIERIA_NO_PROYECTISTA },
      // Fix Z (2026-09-02): órdenes de trabajo de asistencia técnica.
      { path: "/ingenieria/asistencias", label: "Asistencias" },
      // Fix DT (2026-09-22): informes redactados con IA y con el membrete de CEN.
      { path: "/ingenieria/informes", label: "Informes" },
      { path: "/ingenieria/catalogo-materiales", label: "Catálogo de Materiales", roles: INGENIERIA_NO_PROYECTISTA },
      {
        path: "/ingenieria/presupuestos",
        label: "Presupuestos",
        roles: INGENIERIA_NO_PROYECTISTA,
        subItems: [
          { path: "/ingenieria/presupuestos/obra",     label: "De Obra" },
          { path: "/ingenieria/presupuestos/proyecto", label: "De Proyecto" },
          { path: "/ingenieria/presupuestos/varios",   label: "Varios" },
        ],
      },
    ],
  },
  {
    id: "obra",
    label: "Obra",
    icon: HardHat,
    accent: "#EA580C",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "RRHH",
            "Director", "Gerencia Técnica", "Jefe de Calidad", "Jefe de RRHH", "Residente de Obra", "Ingeniería", "Pasantía"],
    path: "/obra",
  },
  {
    id: "inventario",
    label: "Inventario",
    icon: Package,
    accent: "#F59E0B",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras",
            "Director", "Gerencia Técnica", "Jefe de Compras", "Jefe de Logística", "Residente de Obra", "Ingeniería", "Pasantía"],
    children: [
      { path: "/inventario/materiales", label: "Materiales" },
      { path: "/inventario/herramientas", label: "Herramientas" },
      // BLOQUE 35 PARTE B — Alquileres visible solo para roles de gestión (mismo
      // set del backend, routers/alquileres.py::ALQUILER_ROLES).
      { path: "/inventario/alquileres", label: "Alquileres", roles: [
        "Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica",
        "Coordinador de Obra", "Jefe de Compras", "Jefe de Logística",
        "Residente de Obra",   // Fix FH (2026-10-08)
      ] },
    ],
  },
  {
    id: "ventas",
    label: "Ventas",
    icon: BarChart2,
    accent: "#EC4899",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico",
            "Director", "Gerencia Comercial", "Accionista"],
    path: "/ventas",
  },
  {
    id: "rrhh",
    label: "RRHH",
    icon: Users,
    accent: "#EF4444",
    roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "RRHH",
            "Director", "Jefe de RRHH", "Residente de Obra"],
    path: "/rrhh",
  },
  // Auditoría 2026-08-27, CAMBIO 6 — Archivos de Empresa (CEN/ADM), nivel
  // empresa. La sección "Obras" dentro de esta pantalla enlaza a lo que ya
  // existe por obra (Obra > Archivos), sin roles restrictivos adicionales.
  {
    id: "archivos",
    label: "Archivos",
    icon: FolderOpen,
    accent: "#0EA5E9",
    path: "/archivos",
  },
];

const ADMIN_NAV = [
  { path: "/usuarios", label: "Usuarios", icon: UserCog, roles: ["Superadministrador", "Administrador", "Director"] },   // Fix GU (2026-10-17)
  { path: "/auditoria", label: "Auditoría", icon: Shield, roles: ["Superadministrador"] },
];

function hasAccess(roles, userRole) {
  if (!roles) return true;
  if (roles === "*") return true;
  return roleAllowed(userRole, roles);
}

function CENLogo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(-30 20 20)" />
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(30 20 20)" />
      <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M21.5 13L16 21.5H20.5L18.5 27L24 18.5H19.5L21.5 13Z" fill="currentColor" />
    </svg>
  );
}

// ─── COLLAPSIBLE NAV GROUP ────────────────────────────────────
function SubNavItem({ child, groupAccent, onClose }) {
  const location = useLocation();
  const isAnySubActive = child.subItems?.some(s => location.pathname.startsWith(s.path));
  const isSelfActive = location.pathname === child.path;
  const [open, setOpen] = useState(isAnySubActive || isSelfActive);

  return (
    <div>
      <div className="flex items-center">
        <NavLink
          to={child.path}
          onClick={onClose}
          className={`flex-1 flex items-center gap-2 px-2.5 py-2 rounded-l-lg text-[12.5px] font-medium transition-all duration-100 ${
            isSelfActive || isAnySubActive
              ? "text-white bg-white/[0.08]"
              : "text-[#666] hover:text-[#bbb] hover:bg-white/[0.04]"
          }`}
          data-testid={`nav-${child.path.replace(/\//g, "-").slice(1)}`}
        >
          {() => (
            <>
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors"
                style={{ backgroundColor: isAnySubActive || isSelfActive ? groupAccent : "#444" }}
              />
              {child.label}
            </>
          )}
        </NavLink>
        <button
          onClick={() => setOpen(o => !o)}
          className={`px-2 py-2 rounded-r-lg transition-all ${
            isAnySubActive || isSelfActive ? "text-white/70" : "text-[#555] hover:text-[#aaa]"
          } hover:bg-white/[0.04]`}
        >
          <ChevronDown size={11} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="mt-0.5 ml-5 pl-2.5 border-l border-white/[0.06] space-y-0.5">
          {child.subItems.map(sub => (
            <NavLink
              key={sub.path}
              to={sub.path}
              onClick={onClose}
              data-testid={`nav-${sub.path.replace(/\//g, "-").slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11.5px] transition-all duration-100 ${
                  isActive
                    ? "text-white bg-white/[0.07]"
                    : "text-[#555] hover:text-[#aaa] hover:bg-white/[0.03]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className="w-1 h-1 rounded-full flex-shrink-0"
                    style={{ backgroundColor: isActive ? groupAccent : "#333" }}
                  />
                  {sub.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NavGroup({ item, userRole, isOpen, onToggle, onClose, badges }) {
  const location = useLocation();
  const hasActiveChild = item.children?.some(c => {
    if (location.pathname.startsWith(c.path)) return true;
    if (c.subItems) return c.subItems.some(s => location.pathname.startsWith(s.path));
    return false;
  });
  const groupHasBadge = item.children?.some(c => badges?.[c.path] > 0);

  return (
    <div>
      <button
        onClick={onToggle}
        data-testid={`nav-group-${item.id}`}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-all duration-150 group ${
          hasActiveChild
            ? "text-white"
            : "text-[#777] hover:text-[#ccc] hover:bg-white/[0.04]"
        }`}
      >
        <span
          className="w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0 transition-colors"
          style={{ color: hasActiveChild ? item.accent : undefined }}
        >
          <item.icon size={15} strokeWidth={hasActiveChild ? 2 : 1.5} />
        </span>
        <span className="flex-1 text-left uppercase tracking-[0.08em] text-[11px]">{item.label}</span>
        {groupHasBadge && (
          <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" data-testid={`nav-badge-${item.id}`} />
        )}
        <ChevronDown
          size={13}
          className={`flex-shrink-0 transition-transform duration-200 opacity-50 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="mt-0.5 ml-3 pl-3 border-l border-white/[0.07] space-y-0.5 pb-1">
          {item.children.filter((child) => !child.roles || hasAccess(child.roles, userRole)).map((child) => (
            child.subItems ? (
              <SubNavItem key={child.path} child={child} groupAccent={item.accent} onClose={onClose} />
            ) : (
              <NavLink
                key={child.path}
                to={child.path}
                onClick={onClose}
                data-testid={`nav-${child.path.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12.5px] font-medium transition-all duration-100 ${
                    isActive
                      ? "text-white bg-white/[0.08]"
                      : "text-[#666] hover:text-[#bbb] hover:bg-white/[0.04]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors"
                      style={{ backgroundColor: isActive ? item.accent : "#444" }}
                    />
                    {child.label}
                    {badges?.[child.path] > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-testid={`nav-dot-${child.path.replace(/\//g, "-").slice(1)}`} />
                    )}
                  </>
                )}
              </NavLink>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// ─── STANDALONE NAV ITEM ──────────────────────────────────────
function NavStandaloneItem({ item, onClose }) {
  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      data-testid={`nav-${item.id}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-all duration-150 ${
          isActive
            ? "text-white bg-white/[0.08]"
            : "text-[#777] hover:text-[#ccc] hover:bg-white/[0.04]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className="w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0"
            style={{ color: isActive ? item.accent : undefined }}
          >
            <item.icon size={15} strokeWidth={isActive ? 2 : 1.5} />
          </span>
          <span className="uppercase tracking-[0.08em] text-[11px]">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

// ─── SIDEBAR CONTENT ─────────────────────────────────────────
function SidebarContent({ onClose }) {
  const { access: moduleAccess } = useModuleAccess();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initially open sections based on current path
  const getInitialOpen = () => {
    const open = new Set();
    NAV_TREE.forEach((item) => {
      if (item.children?.some((c) => {
        if (location.pathname.startsWith(c.path)) return true;
        if (c.subItems) return c.subItems.some(s => location.pathname.startsWith(s.path));
        return false;
      })) {
        open.add(item.id);
      }
    });
    return open;
  };

  const [openSections, setOpenSections] = useState(getInitialOpen);
  const [alertBadges, setAlertBadges] = useState({});

  // Bloque 31 — Operario Electricista no debe generar tráfico de alertas
  // (evita 403 en loop + ruido de toasts). También se le oculta la campana.
  const isOperario = user?.role === "Operario Electricista" || moduleAccess?.custom;

  // BLOQUE 6 PARTE A — punto rojo en Compras > Servicios cuando haya alquileres por vencer/vencidos
  const fetchAlertBadges = useCallback(async () => {
    if (isOperario) return;
    try {
      const { data } = await axios.get(`${API}/alertas/pendientes`, { withCredentials: true });
      const alquilerCount = (data?.alquileres_por_vencer || 0) + (data?.alquileres_vencidos || 0);
      setAlertBadges({ "/compras/servicios": alquilerCount });
    } catch (_e) { /* silencioso */ }
  }, [isOperario]);

  useEffect(() => {
    if (isOperario) return;
    fetchAlertBadges();
    const id = setInterval(fetchAlertBadges, 60000);
    return () => clearInterval(id);
  }, [fetchAlertBadges, isOperario]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada correctamente");
    navigate("/login");
    onClose?.();
  };

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  const isSuperAdmin = user?.role === "Superadministrador";
  const isProyectista = user?.role === "Proyectista";
  const isResidente = user?.role === "Residente de Obra";
  const isOperarioElectricista = user?.role === "Operario Electricista";
  // PROMPT BLOQUE 2 (Parte B) — el Residente de Obra solo ve Compras > Materiales y Obra.
  // BLOQUE 31 — el Operario Electricista solo ve Compras > Materiales.
  // Fix Z (2026-09-02): el Operario y el Residente además ven Ingeniería → Asistencias
  // (solo ese hijo; el resto de Ingeniería sigue oculto para ellos).
  const legacyTree = isOperarioElectricista
    ? NAV_TREE.filter((item) => ["compras", "ingenieria"].includes(item.id)).map((item) => ({
        ...item,
        children: item.children.filter((c) => item.id === "compras" ? c.path === "/compras/materiales" : c.path === "/ingenieria/asistencias"),
      }))
    : isResidente
    ? NAV_TREE.filter((item) => ["compras", "obra", "rrhh", "ingenieria", "inventario"].includes(item.id)).map((item) =>
        item.id === "compras"
          ? { ...item, children: item.children.filter((c) => c.path === "/compras/materiales") }
          : item.id === "ingenieria"
          ? { ...item, children: item.children.filter((c) => c.path === "/ingenieria/asistencias") }
          // Fix FH (2026-10-08): de Inventario, el Residente solo ve Alquileres.
          : item.id === "inventario"
          ? { ...item, children: item.children.filter((c) => c.path === "/inventario/alquileres") }
          : item
      )
    : NAV_TREE.filter((item) => hasAccess(item.roles, user?.role));
  const expandedTree = moduleAccess?.configured && !isProyectista && !isResidente && !isOperarioElectricista ? NAV_TREE : legacyTree;
  const accessibleTree = expandedTree.filter(item => moduleAccess?.modules[item.id] === true);
  const accessibleAdmin = isOperarioElectricista
    ? []
    : ADMIN_NAV.filter((item) => hasAccess(item.roles, user?.role) && moduleAccess?.modules[item.path.slice(1)] === true);

  return (
    <div className="flex flex-col h-full" style={{ background: "#1E1E1E" }}>
      {/* Logo CEN Real */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.05]">
        <div className="bg-white rounded-xl p-1.5 flex-shrink-0 shadow-sm" style={{ width: 110, height: 40 }}>
          <img
            src="/logo_cen_transparent.png"
            alt="CEN Ingeniería"
            className="w-full h-full object-contain"
          />
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-[#555] hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors lg:hidden ml-auto"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
        {/* Dashboard */}
        {!isProyectista && !isResidente && !isOperarioElectricista && moduleAccess?.modules.dashboard === true && (
        <NavLink
          to="/dashboard"
          onClick={onClose}
          data-testid="nav-dashboard"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-all duration-150 mb-2 ${
              isActive
                ? "bg-[#2E6DA4] text-white shadow-sm"
                : "text-[#777] hover:text-white hover:bg-white/[0.06]"
            }`
          }
        >
          <LayoutDashboard size={15} strokeWidth={1.5} />
          <span>Dashboard</span>
        </NavLink>
        )}

        {/* Divider */}
        <div className="px-3 pb-1 pt-2">
          <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#3D3D3D]">Módulos</p>
        </div>

        {/* Main nav tree */}
        {accessibleTree.map((item) =>
          item.children ? (
            <NavGroup
              key={item.id}
              item={item}
              userRole={user?.role}
              isOpen={openSections.has(item.id)}
              onToggle={() => toggleSection(item.id)}
              onClose={onClose}
              badges={alertBadges}
            />
          ) : (
            <NavStandaloneItem key={item.id} item={item} onClose={onClose} />
          )
        )}

        {/* Admin section */}
        {accessibleAdmin.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#3D3D3D]">Administración</p>
            </div>
            {accessibleAdmin.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.path.replace("/", "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium transition-all duration-150 ${
                    isActive
                      ? "bg-white/[0.08] text-white"
                      : "text-[#666] hover:text-[#ccc] hover:bg-white/[0.04]"
                  }`
                }
              >
                <item.icon size={14} strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Bottom: Settings + User */}
      <div className="border-t border-white/[0.05] px-2 py-2 space-y-1">
        {/* Fix EU (2026-10-01): instalar la plataforma como app */}
        <InstalarAppItem onClose={onClose} />
        {!isProyectista && !isResidente && !isOperarioElectricista && moduleAccess?.modules.configuracion === true && (
        <NavLink
          to="/configuracion"
          onClick={onClose}
          data-testid="nav-configuracion"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium transition-all duration-150 ${
              isActive ? "bg-white/[0.08] text-white" : "text-[#666] hover:text-[#ccc] hover:bg-white/[0.04]"
            }`
          }
        >
          <Settings size={14} strokeWidth={1.5} />
          <span>Configuración</span>
        </NavLink>
        )}

        <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl ${isSuperAdmin ? "bg-amber-500/[0.07]" : "bg-white/[0.02]"}`}>
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm ${
              isSuperAdmin ? "bg-gradient-to-br from-amber-400 to-amber-600" : "bg-[#2E6DA4]"
            }`}
          >
            <span className="text-white text-[10px] font-bold">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[12px] font-semibold truncate leading-tight">{user?.name || "Usuario"}</p>
            <div className="flex items-center gap-1 mt-0.5">
              {isSuperAdmin && <Shield size={8} className="text-amber-400 flex-shrink-0" />}
              <p className={`text-[9px] truncate font-medium ${isSuperAdmin ? "text-amber-400" : "text-[#555]"}`}>
                {user?.role}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-[#444] hover:text-red-400 p-1.5 rounded-lg hover:bg-white/10 transition-all"
            data-testid="logout-btn"
            title="Cerrar sesión"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN LAYOUT ─────────────────────────────────────────────
function titleForPath(path) {
  const buscar = (nodos) => {
    for (const n of nodos || []) {
      if (n.path === path) return n.label;
      const h = buscar(n.children);
      if (h) return h;
    }
    return null;
  };
  const hallado = buscar(NAV_TREE);
  if (hallado) return hallado;
  const ultimo = path.split("/").filter(Boolean).pop() || "Inicio";
  return ultimo.charAt(0).toUpperCase() + ultimo.slice(1).replace(/-/g, " ");
}

export default function Layout({ tabRoutes }) {
  return (
    <TabsProvider titleFor={titleForPath}>
      <LayoutShell tabRoutes={tabRoutes} />
    </TabsProvider>
  );
}

function LayoutShell({ tabRoutes }) {
  const { access: moduleAccess } = useModuleAccess();
  const mainRef = useRef(null);
  const tabs = useTabs();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (window.__cenShowWelcome) {
      const t = setTimeout(() => setShowWelcome(true), 300);
      return () => clearTimeout(t);
    }
  }, []);

  const firstName = user?.name?.split(" ")[0] || "Usuario";
  const isSuperAdmin = user?.role === "Superadministrador";
  useAvisoVersionNueva();   // Fix EU (2026-10-01): avisa cuando se publica una versión nueva

  return (
    <div className="flex h-screen bg-[#F7F8FA] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex flex-col w-[220px] flex-shrink-0 h-screen border-r border-[#1a1a1a]"
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 max-w-[80vw] flex-shrink-0 h-full z-10 shadow-2xl bg-[#1E1E1E]" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="glass border-b border-gray-200/80 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>

          <div className="flex-1 min-w-0">
            {/* Fix EU (2026-10-01): en el celular, el logo en lugar del saludo */}
            <img src="/logo_cen_transparent.png" alt="CEN Ingeniería" className="h-7 w-auto sm:hidden" data-testid="header-logo-movil" />
            <p className="text-sm font-semibold text-gray-800 hidden sm:block" data-testid="header-greeting">
              Hola, {firstName}
              {isSuperAdmin && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                  Superadmin
                </span>
              )}
            </p>
          </div>

          {/* BLOQUE 52 — el Jefe de Compras ya tiene sus alertas en su propio
              dashboard (Bloque 34); la campana le queda redundante. El Operario
              Electricista no tiene acceso al endpoint de alertas (403). Al no
              montarse el componente, tampoco se hace polling para estos roles. */}
          {!moduleAccess?.custom && !["Operario Electricista", "Jefe de Compras"].includes(normalizeRole(user?.role)) && <HeaderBell />}

          <div className="flex items-center gap-2.5 pl-3 border-l border-gray-200/80 flex-shrink-0">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${
                isSuperAdmin ? "bg-gradient-to-br from-amber-400 to-amber-600" : "bg-[#2E6DA4]"
              }`}
            >
              <span className="text-white text-[11px] font-bold">
                {user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "??"}
              </span>
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-semibold text-gray-800 truncate max-w-[120px] leading-tight">{user?.name}</p>
              <p className="text-[10px] text-gray-400 font-medium">{user?.role}</p>
            </div>
          </div>
        </header>

        {/* Fix EU (2026-10-01): sin conexión y aviso para instalar la app en el celular */}
        <BarraSinConexion />
        <AvisoInstalarApp />

        {/* Page content */}
        <TabsBar />
        <main ref={mainRef} className="flex-1 overflow-y-auto" onScroll={e => { tabs.scrolls.current[tabs.activeKey] = e.currentTarget.scrollTop; }}>
          <div>
            {moduleAccess?.custom && <p className="px-4 py-2 text-sm bg-blue-50 text-blue-800" role="status">Perfil de consulta: acceso de lectura a los módulos habilitados.</p>}
            <TabsHost routes={tabRoutes} scrollRef={mainRef} />
          </div>
        </main>
      </div>

      {showWelcome && (
        <WelcomeModal
          user={user}
          onClose={() => { window.__cenShowWelcome = false; setShowWelcome(false); }}
        />
      )}
    </div>
  );
}
